<template>
  <v-container fluid class="pa-6 bg-light-gradient">
    <!-- Header Section -->
    <v-row align="center" class="mb-10">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-icon large color="primary" class="mr-3">mdi-calendar-star</v-icon>
          <h1 class="text-h3 font-weight-black primary--text tracking-tight">
            {{ $t('extracurricular_activities') }}
          </h1>
        </div>
        <p class="text-subtitle-1 grey--text text--darken-2 font-weight-medium">
          {{ $t('msg_view_history_all_personnel') ? 'Gestiona tus actividades y consulta la lista de estudiantes inscritos.' : 'Activities Management' }}
        </p>
        <v-divider class="custom-divider"></v-divider>
      </v-col>
    </v-row>

    <!-- Activities Grid -->
    <v-row v-if="loadingActivities" justify="center" class="my-12">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="74"
          width="6"
        ></v-progress-circular>
        <p class="mt-6 text-h6 primary--text font-weight-medium animate-pulse">Cargando tus actividades...</p>
      </v-col>
    </v-row>

    <v-row v-else-if="activities.length === 0" justify="center" class="my-12">
      <v-col cols="12" sm="8" md="6" class="text-center">
        <div class="empty-state-container pa-8 rounded-xl elevation-2 bg-white">
          <v-icon size="120" color="grey lighten-3">mdi-calendar-remove-outline</v-icon>
          <h2 class="text-h4 grey--text text--darken-1 mt-6 font-weight-bold">
            Sin actividades asignadas
          </h2>
          <p class="text-body-1 grey--text mt-2">
            No tienes actividades extracurriculares programadas en este momento.
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row v-else class="activities-grid">
      <v-col
        v-for="activity in activities"
        :key="activity.idExtracurricularActivity"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="pa-4"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            :elevation="hover ? 16 : 4"
            class="activity-card rounded-xl overflow-hidden transition-all-slow"
            :class="{'on-hover': hover}"
            @click="showStudents(activity)"
          >
            <!-- Vibrant Header Gradient -->
            <div :class="['card-header-gradient', getGradientClass(activity)]">
              <v-icon dark class="activity-icon-floating">mdi-star-face</v-icon>
            </div>
            
            <v-card-text class="pt-8 px-6 pb-4">
              <div class="overline primary--text font-weight-black mb-1">
                #{{ activity.idActivity.name }}
              </div>
              <h2 class="text-h5 font-weight-bold text-dark mb-4 text-truncate-2">
                {{ activity.name || 'Actividad Sin Nombre' }}
              </h2>
              
              <v-chip
                small
                outlined
                color="primary"
                class="font-weight-bold"
              >
                <v-icon left x-small>mdi-account-multiple</v-icon>
                Consultar Alumnos
              </v-chip>
            </v-card-text>
            
            <v-divider class="mx-6 opacity-10"></v-divider>
            
            <v-card-actions class="px-6 py-4">
              <span class="text-caption font-weight-medium grey--text text--darken-1">
                Toca para ver detalles
              </span>
              <v-spacer></v-spacer>
              <v-btn
                fab
                x-small
                color="primary"
                depressed
                class="elevation-2"
              >
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <!-- Students Dialog -->
    <v-dialog
      v-model="dialogStudents"
      max-width="850px"
      scrollable
      transition="scale-transition"
      overlay-opacity="0.8"
      overlay-color="primary darken-4"
    >
      <v-card class="student-list-card glass-card">
        <v-toolbar flat dark color="primary" class="gradient-toolbar px-4">
          <v-avatar color="white" size="40" class="mr-4">
            <v-icon color="primary">mdi-account-group</v-icon>
          </v-avatar>
          <v-toolbar-title class="font-weight-black text-h5 tracking-tight">
            {{ selectedActivity ? selectedActivity.name : 'Estudiantes' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn 
            v-if="showBulkButton && selectedStudents.length > 0"
            color="success" 
            dark 
            depressed
            class="mr-4 rounded-lg font-weight-bold"
            :loading="takingAttendance"
            @click="takeAttendance"
          >
            <v-icon left>mdi-account-check</v-icon>
            Tomar Asistencia ({{ selectedStudents.length }})
          </v-btn>
          <v-btn icon @click="dialogStudents = false" class="bg-white-opacity">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <v-data-table
            v-model="selectedStudents"
            :headers="studentHeaders"
            :items="students"
            :loading="loadingStudents"
            :show-select="showBulkButton"
            item-key="id"
            class="elevation-0 custom-data-table"
            :no-data-text="$t('no_assigned_students')"
            loading-text="Cargando lista de estudiantes..."
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.avatar="{ item }">
              <v-avatar size="50" class="my-3 elevation-4 border-2-white shadow-sm">
                <v-img :src="item.avatar || defaultAvatar" :alt="item.first_name">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0 bg-grey-lighten-4" align="center" justify="center">
                      <v-icon color="grey lighten-1" size="30">mdi-account-outline</v-icon>
                    </v-row>
                  </template>
                </v-img>
              </v-avatar>
            </template>
            
            <template v-slot:item.first_name="{ item }">
              <div class="text-body-1 font-weight-bold text-darker">{{ item.first_name }}</div>
            </template>

            <template v-slot:item.last_name="{ item }">
              <div class="text-body-1 text-grey-darken-2">{{ item.last_name }}</div>
            </template>

            <template v-slot:item.extracurricular_status="{ item }">
              <div class="d-flex justify-center">
                <!-- Status -1: Botón para tomar asistencia -->
                <v-tooltip bottom v-if="item.extracurricular_status === -1">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      fab
                      x-small
                      color="red accent-2"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      elevation="3"
                      :loading="loadingAction === (item.idStudent || item.id)"
                      @click="changeAttendanceStudent(true, item.idStudent || item.id)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('add_assistance') }}</span>
                </v-tooltip>

                <!-- Status 0: Botón para quitar asistencia -->
                <v-tooltip bottom v-else-if="item.extracurricular_status === 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      fab
                      x-small
                      color="success"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      elevation="3"
                      :loading="loadingAction === (item.idStudent || item.id)"
                      @click="changeAttendanceStudent(false, item.idStudent || item.id)"
                    >
                      <v-icon small>mdi-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('remove_assistance') }}</span>
                </v-tooltip>

                <!-- Status diferente a -1 y 0: Mostrar check de "Ya firmado/Salida" -->
                <v-avatar v-else size="36" color="primary lighten-5" class="elevation-1">
                  <v-icon color="primary" small>mdi-check-all</v-icon>
                </v-avatar>
              </div>
            </template>

            <template v-slot:item.action_status="{ item }">
              <div class="text-center font-weight-medium">
                <span v-if="item.extracurricular_status === -1" class="red--text text--darken-2">
                  {{ $t('did_not_attend') }}
                </span>
                
                <v-btn
                  v-else-if="item.extracurricular_status === 0"
                  color="primary"
                  small
                  elevation="2"
                  class="rounded-pill px-4 font-weight-bold"
                  :loading="loadingSign === (item.idStudent || item.id)"
                  @click="signExitPass(item.idStudent || item.id)"
                >
                  <v-icon left small>mdi-pen</v-icon>
                  {{ $t('sign_exit') }}
                </v-btn>

                <span v-else-if="item.extracurricular_status === 1" class="success--text font-weight-bold d-flex align-center justify-center">
                  <v-icon x-small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                  {{ $t('signed') }}
                </span>

                <span v-else-if="item.extracurricular_status === 2" class="blue--text text--darken-2 d-flex align-center justify-center">
                  <v-icon x-small color="blue darken-2" class="mr-1">mdi-exit-run</v-icon>
                  {{ $t('out_of_school') }}
                </span>

                <span v-else-if="item.extracurricular_status === 3" class="orange--text text--darken-3">
                  {{ $t('transferred') }}
                </span>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-10">
                <v-icon size="64" color="grey lighten-2">mdi-account-off-outline</v-icon>
                <div class="text-h6 grey--text mt-4">{{ $t('no_assigned_students') }}</div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-lighten-5">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-8 font-weight-bold rounded-pill elevation-4"
            @click="dialogStudents = false"
            large
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'

export default {
  name: "extracurricular_activities",
  data() {
    return {
      activities: [],
      selectedActivity: null,
      students: [],
      loadingActivities: false,
      loadingStudents: false,
      takingAttendance: false,
      loadingAction: null,
      loadingSign: null,
      dialogStudents: false,
      selectedStudents: [],
      defaultAvatar: require('@/assets/logo_escuela.png'),
      studentHeaders: [
        {
          text: this.$t('photo'),
          align: 'center',
          sortable: false,
          value: 'avatar',
          width: '120px'
        },
        {
          text: this.$t('Apellido'),
          align: 'left',
          sortable: true,
          value: 'last_name',
          class: 'text-overline'
        },
        {
          text: this.$t('Nombre'),
          align: 'left',
          sortable: true,
          value: 'first_name',
          class: 'text-overline'
        },
        {
          text: this.$t('attended'),
          align: 'center',
          sortable: false,
          value: 'extracurricular_status',
          width: '140px'
        },
        {
          text: this.$t('estado'),
          align: 'center',
          sortable: true,
          value: 'action_status',
          width: '160px'
        }
      ]
    }
  },
  computed: {
    showBulkButton() {
      // Button only appears if all students in the list are pending attendance (status -1)
      return this.students.length > 0 && this.students.every(s => s.extracurricular_status === -1);
    }
  },
  created() {
    this.getActivities()
  },
  methods: {
    getActivities() {
      this.loadingActivities = true
      HTTP.get('my_extracurricular_activities/', {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(response => {
        this.activities = response.data
        this.loadingActivities = false
      })
      .catch(error => {
        console.error('Error fetching activities:', error)
        this.loadingActivities = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'No se pudieron cargar las actividades.')
      })
    },
    showStudents(activity) {
      this.selectedActivity = activity
      this.dialogStudents = true
      this.selectedStudents = []
      this.getStudents(activity.idExtracurricularActivity)
    },
    getStudents(activityId) {
      this.loadingStudents = true
      this.students = []
      HTTP.get(`students_by_extracurricular_activity/${activityId}`, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(response => {
        // Filter students with status 3 (transferred)
        this.students = response.data.filter(student => student.status === 3 || student.extracurricular_status > 0)
        this.loadingStudents = false
      })
      .catch(error => {
        console.error('Error fetching students:', error)
        this.loadingStudents = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al obtener la lista de alumnos.')
      })
    },
    takeAttendance() {
      if (this.selectedStudents.length === 0) return

      this.takingAttendance = true
      const studentIds = this.selectedStudents.map(s => s.idStudent || s.id)

      HTTP.post('transferred-extracurricular/take-attendance/', 
        { students: studentIds },
        {
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          }
        }
      )
      .then(response => {
        this.takingAttendance = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', response.data.message || 'Asistencia tomada con éxito.')
        
        // Refresh the list
        this.getStudents(this.selectedActivity.idExtracurricularActivity)
        this.selectedStudents = []
      })
      .catch(error => {
        console.error('Error taking attendance:', error)
        this.takingAttendance = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al tomar asistencia.')
      })
    },
    changeAttendanceStudent(take, studentId) {
      this.loadingAction = studentId
      const endpoint = take ? 'take-attendance/' : 'remove-attendance/'
      
      HTTP.post(`transferred-extracurricular/${studentId}/${endpoint}`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(() => {
        this.getStudents(this.selectedActivity.idExtracurricularActivity)
        this.loadingAction = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', take ? 'Asistencia registrada con éxito.' : 'Asistencia removida correctamente.')
      })
      .catch(error => {
        console.error('Error changing attendance:', error)
        this.loadingAction = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al procesar la asistencia.')
      })
    },
    signExitPass(studentId) {
      this.loadingSign = studentId
      
      HTTP.post(`transferred-extracurricular/${studentId}/sign-pass/`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(() => {
        this.getStudents(this.selectedActivity.idExtracurricularActivity)
        this.loadingSign = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Pase de salida firmado correctamente.')
      })
      .catch(error => {
        console.error('Error signing exit pass:', error)
        this.loadingSign = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al firmar el pase de salida.')
      })
    },
    getGradientClass(activity) {
      if (activity.idActivity && activity.idActivity.workshop) {
        return 'grad-green';
      } else if (activity.idActivity && activity.idActivity.sport) {
        return 'grad-blue';
      }
      
      // Fallback to random/indexed gradients if properties are missing
      const index = this.activities.indexOf(activity);
      const gradients = ['grad-1', 'grad-2', 'grad-3', 'grad-4', 'grad-5', 'grad-6'];
      return gradients[index % gradients.length];
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap');

.bg-light-gradient {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif !important;
}

.tracking-tight {
  letter-spacing: -0.05em;
}

.custom-divider {
  width: 80px;
  border-width: 3px;
  border-radius: 5px;
  border-color: var(--v-primary-base) !important;
  opacity: 1;
}

.activity-card {
  cursor: pointer;
  position: relative;
  border: none;
  background: white;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.on-hover {
  transform: translateY(-8px);
}

.card-header-gradient {
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.activity-icon-floating {
  font-size: 60px !important;
  opacity: 0.3;
  transform: rotate(-15deg);
  position: absolute;
  right: -10px;
  bottom: -10px;
}

.grad-1 { background: linear-gradient(45deg, #FF6B6B 0%, #FF8E53 100%); }
.grad-2 { background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%); }
.grad-3 { background: linear-gradient(45deg, #667eea 0%, #764ba2 100%); }
.grad-4 { background: linear-gradient(45deg, #43e97b 0%, #38f9d7 100%); }
.grad-5 { background: linear-gradient(45deg, #fa709a 0%, #fee140 100%); }
.grad-6 { background: linear-gradient(45deg, #30cfd0 0%, #330867 100%); }

/* Specific category gradients */
.grad-green { background: linear-gradient(45deg, #10b981 0%, #34d399 100%); }
.grad-blue { background: linear-gradient(45deg, #2563eb 0%, #60a5fa 100%); }

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  min-height: 2.4em;
}

.gradient-toolbar {
  background: linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%) !important;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
}

.bg-white-opacity {
  background: rgba(255, 255, 255, 0.1);
}

.border-2-white {
  border: 4px solid white;
}

.shadow-sm {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.custom-data-table >>> thead th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.custom-data-table >>> tbody tr:hover {
  background-color: #f1f5f9 !important;
}

.text-dark { color: #1e293b; }
.text-darker { color: #0f172a; }

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
}

.transition-all-slow {
  transition: all 0.5s ease;
}

.empty-state-container {
  border: 2px dashed #e2e8f0;
}
</style>
